import React, { useContext, useEffect, useState, useRef } from 'react';
import moment from 'moment';

import { BaseDraggableWidget } from '../Card';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';

import { getAuditTrails } from '../../../api/auditTrails';
import SettingsContext from '../../../context/SettingsContext';

import { classNames } from '../../helpers/functions';

// Action verb mapping for human-readable messages
const actionVerbs = {
	CREATE: 'created',
	UPDATE: 'updated',
	DELETE: 'deleted',
	PUBLISH: 'published',
	APPROVE: 'approved',
	ARCHIVE: 'archived',
	RESTORE: 'restored',
	SIGN: 'signed',
};

// Entity type display names
const entityTypeLabels = {
	asset: 'Asset',
	ticket: 'Ticket',
	sop: 'SOP',
	work_order: 'Work Order',
	checklist_item: 'Checklist Item',
	contract: 'Contract',
	document: 'Document',
	user: 'User',
	pm_schedule: 'PM Schedule',
};

// Color coding by action type
const actionColors = {
	CREATE: 'border-l-4 border-green-300',
	UPDATE: 'border-l-4 border-blue-300',
	DELETE: 'border-l-4 border-red-300',
	PUBLISH: 'border-l-4 border-purple-300',
	APPROVE: 'border-l-4 border-green-300',
	ARCHIVE: 'border-l-4 border-gray-300',
	RESTORE: 'border-l-4 border-teal-300',
	SIGN: 'border-l-4 border-indigo-300',
};

// Action badge colors
const actionBadgeColors = {
	CREATE: 'bg-green-100 text-green-700',
	UPDATE: 'bg-blue-100 text-blue-700',
	DELETE: 'bg-red-100 text-red-700',
	PUBLISH: 'bg-purple-100 text-purple-700',
	APPROVE: 'bg-green-100 text-green-700',
	ARCHIVE: 'bg-gray-100 text-gray-700',
	RESTORE: 'bg-teal-100 text-teal-700',
	SIGN: 'bg-indigo-100 text-indigo-700',
};

// Format a single change for display
const formatChange = (field, change) => {
	const fromValue = change.from ?? 'empty';
	const toValue = change.to ?? 'empty';

	// Truncate long values
	const truncate = (val) => {
		const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
		return str.length > 30 ? str.substring(0, 30) + '...' : str;
	};

	return `${field}: "${truncate(fromValue)}" → "${truncate(toValue)}"`;
};

// Get primary change summary for updates
const getChangeSummary = (changes) => {
	if (!changes || typeof changes !== 'object') return null;

	const keys = Object.keys(changes);
	if (keys.length === 0) return null;

	// Show first change and count of others
	const firstChange = formatChange(keys[0], changes[keys[0]]);
	if (keys.length === 1) return firstChange;

	return `${firstChange} (+${keys.length - 1} more)`;
};

const LiveFeed = ({ title, isPreview, ...rest }) => {
	const { setOpen: openTicketOverview } = useModal('ticketOverview');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openAssetOverview } = useModal('assetOverview');
	const { setOpen: openSopOverview } = useModal('sopOverview');

	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const hasSettings = settings?.user?.widgetSettings
		? settings?.user?.widgetSettings[rest?.ctx?.data?.id]?.feedFilter
		: null;

	const [selectedFilter, setSelectedFilter] = useState(hasSettings || 'all');
	const pollingRef = useRef(null);

	const {
		value: auditTrails,
		execute: refreshFeed,
		error: hasError,
		status: isLoadingFeed,
	} = useAsync(getAuditTrails, {
		defaultPayload: { limit: 20 },
	});

	// Polling for updates every 30 seconds
	useEffect(() => {
		pollingRef.current = setInterval(() => {
			const payload = { limit: 20 };
			if (selectedFilter !== 'all') {
				payload.action = selectedFilter;
			}
			refreshFeed(payload);
		}, 30000);

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current);
			}
		};
	}, [selectedFilter]);

	// Refresh when filter changes
	useEffect(() => {
		const payload = { limit: 20 };
		if (selectedFilter !== 'all') {
			payload.action = selectedFilter;
		}
		refreshFeed(payload);
	}, [selectedFilter]);

	const handleUpdateSettings = (key, value) => {
		const widgetSettings = settings?.user?.widgetSettings;

		let updatedValue = {};

		if (widgetSettings) {
			if (widgetSettings[rest?.ctx?.data?.id]) {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: {
						...widgetSettings[rest?.ctx?.data?.id],
						[key]: value,
					},
				};
			} else {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: { [key]: value },
				};
			}
		} else {
			updatedValue = {
				[rest?.ctx?.data?.id]: { [key]: value },
			};
		}
		updateSettings(
			{
				type: 'user',
				key: 'widgetSettings',
				value: updatedValue,
			},
			{ showNotification: false },
		);
	};

	const handleItemClick = (entry) => {
		const { entityType, entityId } = entry;

		switch (entityType) {
			case 'ticket':
				openTicketOverview({ id: entityId });
				break;
			case 'work_order':
				openWorkOrderOverview({ id: entityId });
				break;
			case 'asset':
				openAssetOverview({ id: entityId });
				break;
			case 'sop':
				openSopOverview({ id: entityId });
				break;
			default:
				// No navigation for other entity types
				break;
		}
	};

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title="Live Feed"
				category="Activity"
				description="Real-time activity feed across your organization"
				{...rest}
				isPreview={true}
			/>
		);
	}

	const entries = auditTrails?.rows || auditTrails || [];

	const isEmpty =
		entries?.length === 0
			? { isEmpty: true, flexCenter: true, skipIcon: true }
			: {};
	const isError = !!hasError ? { isError: true, flexCenter: true } : {};

	return (
		<BaseDraggableWidget
			title="Live Feed"
			widgetProps={{
				isLoading: isLoadingFeed === 'pending',
				...isError,
				...isEmpty,
				isEmptyMessage: 'No activity recorded yet',
				options: [
					{ label: 'All Activity', value: 'all' },
					{ label: 'Creates', value: 'CREATE' },
					{ label: 'Updates', value: 'UPDATE' },
					{ label: 'Deletes', value: 'DELETE' },
				],
				optionSelected: selectedFilter,
				optionsOnChange: (index, value, raw) => {
					setSelectedFilter(raw?.value);
					handleUpdateSettings('feedFilter', raw?.value);
				},
			}}
			{...rest}
		>
			<div className="divide-y divide-gray-100">
				{entries?.length
					? entries?.map((entry) => {
							const actionVerb = actionVerbs[entry.action] || entry.action?.toLowerCase();
							const entityLabel = entityTypeLabels[entry.entityType] || entry.entityType;
							const changeSummary = entry.action === 'UPDATE' ? getChangeSummary(entry.changes) : null;
							const isClickable = ['ticket', 'work_order', 'asset', 'sop'].includes(entry.entityType);

							return (
								<div
									key={entry.id}
									className={classNames(
										'py-3 pl-4 pr-2',
										actionColors[entry.action] || 'border-l-4 border-gray-200',
										isClickable ? 'cursor-pointer hover:bg-gray-50' : '',
									)}
									onClick={() => isClickable && handleItemClick(entry)}
								>
									{/* Main message */}
									<div className="text-sm text-gray-900">
										<span className="font-medium">{entry.actorName || 'System'}</span>
										{' '}
										<span className="text-gray-600">{actionVerb}</span>
										{' '}
										<span className="text-gray-600">{entityLabel}</span>
										{entry.entityName && (
											<>
												{' '}
												<span className="font-medium">"{entry.entityName}"</span>
											</>
										)}
									</div>

									{/* Change summary for updates */}
									{changeSummary && (
										<div className="text-xs text-gray-500 mt-1 truncate">
											{changeSummary}
										</div>
									)}

									{/* Metadata row */}
									<div className="flex items-center gap-2 mt-1.5">
										<span
											className={classNames(
												'inline-flex items-center px-1.5 py-0.5 rounded text-xxs font-medium',
												actionBadgeColors[entry.action] || 'bg-gray-100 text-gray-700',
											)}
										>
											{entry.action}
										</span>
										<span className="text-xxs text-gray-400">
											{moment(entry.createdAt).fromNow()}
										</span>
									</div>
								</div>
							);
						})
					: []}
			</div>
		</BaseDraggableWidget>
	);
};

export default LiveFeed;
